import { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabaseClient'
import {
  Clock,
  Play,
  Award,
  BookOpen
} from 'lucide-react'

interface Tentativa {
  id: string
  teste_id: string
  nota: number
  acertos: number
  total_questoes: number
  created_at: string
  teste?: {
    title: string
  }
}

interface Teste {
  id: string
  title: string
  description?: string
  tempo_limite?: number
  questions_count?: number
}

const SimuladoPage = () => {
  const { profile, isAuthenticated, loading, user } = useAuth()
  const navigate = useNavigate()

  const [availableTestes, setAvailableTestes] = useState<Teste[]>([])
  const [tentativas, setTentativas] = useState<Tentativa[]>([])
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [user])

  const fetchDashboardData = async () => {
    if (!user) return

    try {
      setLoadingData(true)

      // 1. Fetch Testes via Turmas (Restricted Access)
      const { data: membershipData, error: membershipError } = await supabase
        .from('turma_alunos')
        .select(`
          turma:turmas (
            turma_testes (
              teste:testes (id, title, description, tempo_limite)
            )
          )
        `)
        .eq('aluno_id', user.id)

      if (membershipError) throw membershipError

      // Flatten testes from turmas
      const testesMap = new Map<string, any>()
      membershipData?.forEach((membership: any) => {
        membership.turma?.turma_testes?.forEach((tt: any) => {
          if (tt.teste) {
            testesMap.set(tt.teste.id, tt.teste)
          }
        })
      })
      
      const testesData = Array.from(testesMap.values())

      // 2. Fetch User Attempts with Test Title joined
      const { data: tentativasData, error: tentativasError } = await supabase
        .from('tentativas_testes')
        .select('*, teste:testes(title)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (tentativasError) throw tentativasError

      // 3. Get Question Counts for Testes
      const testesWithCounts = await Promise.all((testesData || []).map(async (t: any) => {
        const { count } = await supabase
          .from('questoes')
          .select('*', { count: 'exact', head: true })
          .eq('teste_id', t.id)
        return { ...t, questions_count: count || 0 }
      }))

      setAvailableTestes(testesWithCounts)
      setTentativas(tentativasData || [])

    } catch (error) {
      console.error('Error fetching dashboard:', error)
    } finally {
      setLoadingData(false)
    }
  }

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated() || !profile || profile.role !== 'aluno') {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 shadow-bizu-lg text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-500/10 rounded-full -ml-32 -mb-32 blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 relative z-10">
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Olá, {profile.full_name?.split(' ')[0] || 'Aluno'}</h1>
            <p className="text-primary-100 mt-4 text-lg md:text-xl max-w-xl font-medium leading-relaxed">
              Seu caminho para a aprovação começa aqui. {tentativas.length > 0 ? 'Continue seu excelente trabalho!' : 'Vamos começar seu primeiro simulado?'}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center px-8 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl">
              <div className="text-4xl font-black text-secondary-400">{tentativas.length}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 mt-1">Simulados Concluídos</div>
            </div>
          </div>
        </div>
      </div>

      {/* Simulados Disponíveis */}
      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary-600" />
          Simulados Disponíveis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {availableTestes.length === 0 ? (
            <p className="text-gray-500 col-span-full bg-white p-8 rounded-lg border border-dashed border-gray-300 text-center">Nenhum simulado disponível no momento.</p>
          ) : (
            availableTestes.map(teste => (
              <div key={teste.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-bizu-lg transition-all duration-300 p-8 flex flex-col h-full border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary-50 rounded-bl-full -mr-12 -mt-12 transition-all group-hover:bg-primary-100 group-hover:scale-110" />
                
                <h3 className="font-bold text-xl text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">{teste.title}</h3>
                {teste.description && (
                  <p className="text-sm text-gray-600 mb-6 flex-grow line-clamp-3 leading-relaxed">{teste.description}</p>
                )}

                <div className="flex items-center gap-4 text-xs font-semibold text-gray-500 mb-8 mt-auto">
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                    <BookOpen className="w-4 h-4 text-primary-500" />
                    <span>{teste.questions_count} questões</span>
                  </div>
                  {teste.tempo_limite && (
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                      <Clock className="w-4 h-4 text-secondary-500" />
                      <span>{teste.tempo_limite} min</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate(`/teste/${teste.id}`)}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Iniciar Agora
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Histórico */}
      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary-600" />
          Seu Desempenho
        </h2>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {tentativas.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500">Você ainda não realizou nenhum simulado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50/50 text-gray-500 font-bold uppercase text-xs tracking-wider border-b border-gray-100">
                  <tr>
                    <th className="p-6">Simulado</th>
                    <th className="p-6">Data de Realização</th>
                    <th className="p-6 text-center">Acertos</th>
                    <th className="p-6 text-center">Pontuação TRI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tentativas.map((tentativa) => (
                    <tr key={tentativa.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-6">
                        <div className="font-bold text-gray-900 text-base">
                          {tentativa.teste?.title || 'Simulado Removido'}
                        </div>
                      </td>
                      <td className="p-6 text-gray-600">
                        {new Date(tentativa.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </td>
                      <td className="p-6 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="font-bold text-lg text-gray-900">{tentativa.acertos}</span>
                          <span className="text-[10px] uppercase font-bold text-gray-400">de {tentativa.total_questoes}</span>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <span className={`inline-flex items-center justify-center px-4 py-1.5 rounded-full text-sm font-bold shadow-sm ${tentativa.nota >= 350 ? 'bg-green-100 text-green-700' : 'bg-secondary-100 text-secondary-700'
                          }`}>
                          {tentativa.nota}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default SimuladoPage